import React, {useState} from 'react';
import {ServiceProvider} from '../models/serviceProvider.model';
import AppTable, {TableColumn} from '../../../Presentation/components/ui/AppTable';
import AppTableHeaderCell from '../../../Presentation/components/ui/AppTableHeaderCell';
import {AppText, AppErrorText} from '../../../Presentation/components/ui/Typography';
import {Edit, Trash2, Eye} from 'lucide-react';
import Modal from '../../../Presentation/components/ui/Modal';
import Button from '../../../Presentation/components/ui/Button';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';
import DateTimeDisplay from '../../../Presentation/components/ui/DateTimeDisplay';
import AssignedBrandsCell from './AssignedBrandsCell';
import {BaseTableProps} from '../../../types/table.types';
import {useTableExport, useExportConfig} from '../../../Presentation/hooks/useTableExport';
import ExportButton from '../../../Presentation/components/ui/ExportButton';
import {ExportFormat} from '../../../types/export.types';
import {useAuthorization} from '../../auth/hooks/useAuthorization';
import {PERMISSIONS} from '../../../constants/permissions';
import {ExportFormatters} from '../../../Presentation/utils/exportFormatters';

interface ServiceProviderTableProps extends BaseTableProps<ServiceProvider> {
  onViewDetails?: (id: string) => void;
  onEdit?: (provider: ServiceProvider) => void;
  onDelete?: (id: string) => void;
  exportable?: boolean;
  loading?: boolean;
}

const ServiceProviderTable: React.FC<ServiceProviderTableProps> = ({
  data: serviceProviders,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  showPageSizeSelector = true,
  showPaginationInfo = true,
  onViewDetails,
  onEdit,
  onDelete,
  exportable = false,
  loading = false
}) => {
  const {hasPermission} = useAuthorization();
  const [deleteProvider, setDeleteProvider] = useState<ServiceProvider | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Export configuration
  const {createConfig} = useExportConfig<ServiceProvider>();

  const exportConfig = createConfig([
    {key: 'id', title: 'معرف مزود الخدمة'},
    {key: 'username', title: 'اسم المستخدم'},
    {key: 'fullName', title: 'الاسم الكامل'},
    {key: 'email', title: 'البريد الإلكتروني'},
    {key: 'phoneNumber', title: 'رقم الهاتف'},
    {key: 'companyName', title: 'اسم الشركة'},
    {key: 'website', title: 'الموقع الإلكتروني'},
    {key: 'description', title: 'الوصف'},
    {key: 'status', title: 'الحالة', formatter: ExportFormatters.activeStatus},
    {key: 'createdAt', title: 'تاريخ الإنشاء', formatter: ExportFormatters.dateTime},
    {key: 'updatedAt', title: 'آخر تحديث', formatter: ExportFormatters.dateTime}
  ], 'مزودي_الخدمة', 'تصدير بيانات مزودي الخدمة', 'تصدير جميع بيانات مزودي الخدمة مع التفاصيل الكاملة');

  // Export hook
  const {exportData, isExporting, canExport, recordCount} = useTableExport({
    data: serviceProviders,
    config: exportConfig
  });

  // Handle export
  const handleExport = async (format: ExportFormat) => {
    await exportData(format);
  };


  const handleDelete = async () => {
    if (!deleteProvider || !onDelete) {return;}

    setDeleteLoading(true);
    try {
      await onDelete(deleteProvider.id);
      // Success notification is handled by the store
      setDeleteProvider(null);
    } catch (err) {
      // Let StandardizedErrorHandler handle the error notification
      StandardizedErrorHandler.getInstance().handleError(err);
      // Don't close the modal on error, let user try again or cancel
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns: TableColumn<ServiceProvider>[] = [
    {
      key: 'id',
      title: <AppTableHeaderCell label="رقم مزود الخدمة" />,
      render: (provider: ServiceProvider) => (
        <AppText>{provider.id}</AppText>
      )
    },
    {
      key: 'assignedBrands',
      title: <AppTableHeaderCell label="البراندات المرتبطة" />,
      className: 'min-w-[200px]',
      render: (provider: ServiceProvider) => (
        <AssignedBrandsCell
          providerId={provider.id}
        />
      )
    },
    {
      key: 'displayName',
      title: <AppTableHeaderCell label="الاسم الكامل" />,
      render: (provider: ServiceProvider) => (
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10">
            <div className="h-10 w-10 rounded-full bg-brand-100 flex items-center justify-center">
              <span className="text-brand-600 font-semibold text-sm">
                {provider.displayName.charAt(0).toUpperCase()}
              </span>
            </div>
          </div>
          <div className="mr-4">
            <div className="text-sm font-medium text-gray-900">
              <AppText>{provider.displayName}</AppText>
            </div>
            <div className="text-sm text-gray-500">
              <AppText>@{provider.username}</AppText>
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'email',
      title: <AppTableHeaderCell label="البريد الإلكتروني" />,
      render: (provider: ServiceProvider) => <AppText>{provider.email}</AppText>
    },
    {
      key: 'phoneNumber',
      title: <AppTableHeaderCell label="رقم الهاتف" />,
      render: (provider: ServiceProvider) => <AppText>{provider.phoneNumber}</AppText>
    },
    {
      key: 'status',
      title: <AppTableHeaderCell label="الحالة" />,
      render: (provider: ServiceProvider) => (
        provider.status === 'active'
          ? <AppText className="font-bold text-green-600">{provider.statusText}</AppText>
          : <AppErrorText className="font-bold">{provider.statusText}</AppErrorText>
      )
    },
    {
      key: 'createdAt',
      title: <AppTableHeaderCell label="تاريخ الإنشاء" />,
      render: (provider: ServiceProvider) => (
        provider.createdAt ? <DateTimeDisplay dateString={provider.createdAt.toISOString()} /> : <AppText className="text-gray-500">-</AppText>
      )
    }

  ];

  return (
    <div className="flex-1 min-h-[calc(100vh-80px)] flex flex-col">
      {/* Export Button */}
      {exportable && (
        <div className="mb-4 flex justify-end">
          <ExportButton
            onExport={handleExport}
            isExporting={isExporting}
            canExport={canExport}
            recordCount={recordCount}
            showDropdown={true}
          />
        </div>
      )}

      <AppTable
        columns={columns}
        data={serviceProviders}
        rowKey={(row, index) => `provider-${row.id}-${index || 0}`}
        emptyText="لا توجد مزودي خدمات"
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        showPageSizeSelector={showPageSizeSelector}
        showPaginationInfo={showPaginationInfo}
        loading={loading}
        loadingRows={pageSize || 10}
        loadingMessage="جاري تحميل مزودي الخدمة..."
        actions={(provider) => {
          const actions = [];

          // View Details - Basic permission
          if (onViewDetails && hasPermission(PERMISSIONS.VIEW_DASHBOARD)) {
            actions.push({
              label: 'عرض التفاصيل',
              icon: <Eye className="text-brand-600" size={20} />,
              onClick: () => onViewDetails(provider.id)
            });
          }

          // Edit Provider - Requires edit permission
          if (onEdit && hasPermission(PERMISSIONS.MANAGE_SERVICE_PROVIDERS)) {
            actions.push({
              label: 'تعديل بيانات مزود الخدمة',
              icon: <Edit className="text-brand-600" size={20} />,
              onClick: () => onEdit(provider)
            });
          }

          // Delete Provider - Requires delete permission
          if (hasPermission(PERMISSIONS.MANAGE_SERVICE_PROVIDERS)) {
            actions.push({
              label: 'حذف مزود الخدمة',
              icon: <Trash2 className="text-red-600" size={20} />,
              onClick: () => setDeleteProvider(provider),
              danger: true
            });
          }

          return actions;
        }}
      />
      <Modal open={!!deleteProvider} onClose={() => setDeleteProvider(null)} title="تأكيد الحذف">
        <div className="text-center mb-6">
          هل أنت متأكد من أنك تريد حذف مزود الخدمة؟<br />
          <span className="font-bold text-red-600">{deleteProvider?.displayName}</span>
        </div>
        <div className="flex gap-4 justify-center">
          <Button variant="ghost" onClick={() => setDeleteProvider(null)} disabled={deleteLoading}>
            إلغاء
          </Button>
          <Button variant="danger" onClick={handleDelete} isLoading={deleteLoading}>
            حذف
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default ServiceProviderTable;
